/**
 * Scenario Card Component
 *
 * Card displaying a demo scenario with trigger and reset buttons.
 */

import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Box,
  Chip,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RestoreIcon from '@mui/icons-material/Restore';
import { format } from 'date-fns';

export interface ScenarioCardProps {
  title: string;
  description: string;
  icon?: React.ReactNode;
  onTrigger: () => void;
  onReset?: () => void;
  isExecuting: boolean;
  isResetting: boolean;
  hasBeenExecuted: boolean;
  executedAt?: string | null;
  disabled?: boolean;
  color?: 'primary' | 'secondary' | 'info';
}

export function ScenarioCard({
  title,
  description,
  icon,
  onTrigger,
  onReset,
  isExecuting,
  isResetting,
  hasBeenExecuted,
  executedAt,
  disabled = false,
  color = 'primary',
}: ScenarioCardProps) {
  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: disabled ? 'none' : 'translateY(-4px)',
          boxShadow: disabled ? 'none' : 4,
        },
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
          {icon && (
            <Box
              sx={{
                color: `${color}.main`,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {icon}
            </Box>
          )}
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" component="h3" gutterBottom>
              {title}
            </Typography>
            {hasBeenExecuted && executedAt && (
              <Chip
                label={`Last run: ${format(new Date(executedAt), 'MMM d, h:mm a')}`}
                size="small"
                color="success"
                sx={{ mb: 1 }}
              />
            )}
          </Box>
        </Box>

        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      </CardContent>

      <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
        <Button
          variant="contained"
          color={color}
          onClick={onTrigger}
          disabled={disabled || isExecuting || isResetting}
          startIcon={<PlayArrowIcon />}
          fullWidth={!hasBeenExecuted}
        >
          {isExecuting ? 'Running...' : 'Trigger Scenario'}
        </Button>

        {hasBeenExecuted && onReset && (
          <Button
            variant="outlined"
            color="secondary"
            onClick={onReset}
            disabled={disabled || isExecuting || isResetting}
            startIcon={<RestoreIcon />}
            sx={{ ml: 1 }}
          >
            {isResetting ? 'Resetting...' : 'Reset'}
          </Button>
        )}
      </CardActions>
    </Card>
  );
}
